import { observer } from 'mobx-react-lite';
import * as React from 'react';
import type AttachmentPreview from 'owa-service/lib/contract/AttachmentPreview';
import { getUserMailboxInfo, ClientItemId } from 'owa-client-ids';
import { AttachmentDocView } from 'owa-attachment-view';
import { DefaultButton } from '@fluentui/react/lib/Button';
import classNames from 'classnames';
import * as styles from './styles.scss';
import { KeyCodes } from '@fluentui/react/lib/Utilities';

export default observer(function AttachmentDocContainer({
    itemId,
    attachments,
    maxResults,
    showInlineAttachments,
}: {
    itemId: string;
    attachments: AttachmentPreview[];
    maxResults?: number;
    showInlineAttachments?: boolean;
}) {
    const [expand, setExpand] = React.useState<boolean>(false);
    const toggleExpand = React.useCallback(() => {
        setExpand(!expand);
    }, [expand]);

    const onKeyDown = (evt: React.KeyboardEvent<unknown>) => {
        if (evt.which == KeyCodes.enter || evt.which == KeyCodes.space) {
            evt.preventDefault();
            evt.stopPropagation();
            toggleExpand();
            // TODO: Set focus back to original mail search item
        }
    };

    const filteredAttachments = filterAttachments(attachments, !!showInlineAttachments);
    const attachmentsToShow = expand
        ? filteredAttachments
        : filteredAttachments.slice(0, maxResults);
    const isCollapsed = !!maxResults && filteredAttachments.length > maxResults && !expand;

    return (
        <div className={styles.attachmentWell}>
            {attachmentsToShow?.map((attachmentPreview, index) => {
                if (!attachmentPreview.Attachment || !attachmentPreview.Attachment.AttachmentId) {
                    return null;
                }

                const clientItemId: ClientItemId = {
                    Id: attachmentPreview.ParentItemId?.Id ?? itemId,
                    mailboxInfo: getUserMailboxInfo(),
                };
                const isLastItem = index === attachmentsToShow.length - 1;

                return (
                    <div
                        key={attachmentPreview.Attachment.AttachmentId.Id}
                        className={classNames(styles.attachmentContainer, {
                            [styles.attachmentCollapsed]: isCollapsed && isLastItem,
                            [styles.lastAttachment]: isLastItem,
                        })}>
                        <AttachmentDocView
                            parentItemId={clientItemId}
                            attachment={attachmentPreview.Attachment}
                            isShortDocView={true}
                            useCloudIcon={false}
                            showInfoIcon={index === 0}
                        />
                        {isCollapsed && isLastItem && !!maxResults && (
                            <DefaultButton
                                className={styles.attachmentExpandButton}
                                onClick={toggleExpand}
                                text={`+${filteredAttachments.length - maxResults}`}
                                onKeyDown={onKeyDown}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
});

function filterAttachments(
    attachments: AttachmentPreview[],
    showInlineAttachments: boolean
): AttachmentPreview[] {
    return !showInlineAttachments
        ? attachments.filter(attachment => !attachment.Attachment?.IsInline)
        : attachments;
}
